import { IDetailTodo } from "interfaces/live.interface";
import {
  OrderStatistics,
  OrderStatus,
  OrderSummary,
  TableRow,
} from "interfaces/toss.interface";
import { statusColumnKeys } from "./tossDetailHandler";

export const orderStatusTransitions: Record<OrderStatus, OrderStatus[]> = {
  REQUEST: ["CANCEL"],
  DEPOSIT_WAIT: ["CANCEL"],
  COMPLETE: ["DELIVERY_READY", "DELIVERY", "REFUND", "REFUND_HOLD_CANCEL"],
  DELIVERY_READY: ["COMPLETE", "DELIVERY"],
  DELIVERY: ["DELIVERY_READY", "REFUND_HOLD_RETURN"],
  CANCEL: ["REQUEST"],
  REFUND_HOLD_CANCEL: ["COMPLETE", "REFUND"],
  REFUND_HOLD_RETURN: ["DELIVERY", "REFUND"],
  REFUND: [],
};

// extends OrderStatus | 'TOTAL' // 전체
export type OrderStatusWithTotal = OrderStatus | "TOTAL";

export const orderDisplayGroups: {
  label: string;
  order: number;
  keys: OrderStatusWithTotal[];
  subTabs?: {
    label: string;
    key: OrderStatus;
  }[];
}[] = [
  { label: "신규주문", order: 1, keys: ["REQUEST"] },
  // { label: '입금대기', order: 2, keys: ['DEPOSIT_WAIT'] },
  { label: "결제완료", order: 3, keys: ["COMPLETE"] },
  { label: "배송준비", order: 4, keys: ["DELIVERY_READY"] },
  { label: "배송완료", order: 5, keys: ["DELIVERY"] },
  { label: "주문취소", order: 6, keys: ["CANCEL"] },
  {
    label: "환불보류",
    order: 7,
    keys: ["REFUND_HOLD_CANCEL", "REFUND_HOLD_RETURN"],
    subTabs: [
      { label: "취소", key: "REFUND_HOLD_CANCEL" },
      { label: "반품", key: "REFUND_HOLD_RETURN" },
    ],
  },
  { label: "환불완료", order: 8, keys: ["REFUND"] },
  { label: "전체", order: 9, keys: ["TOTAL"] },
];

// 주문 상태 전환 가능 여부 확인
export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return orderStatusTransitions[from]?.includes(to) ?? false;
}

// 그룹 찾기
export function findGroupByStatus(status: OrderStatus) {
  return orderDisplayGroups.find((group) => group.keys.includes(status));
}

// 개수별 데이터를 그룹화
export const getOrderCountByStatus = (data: OrderStatistics) => {
  //@ts-ignore
  const result: OrderSummary = {
    REQUEST: {
      count: data.orderPlacedCount,
      peopleCount: data.orderPlacedPeopleCount,
    },
    DEPOSIT_WAIT: {
      count: data.depositWaitCount,
      peopleCount: data.depositWaitPeopleCount,
    },
    COMPLETE: {
      count: data.paymentConfirmedCount,
      peopleCount: data.paymentConfirmedPeopleCount,
    },
    DELIVERY_READY: {
      count: data.deliveryReadyCompletedCount,
      peopleCount: data.deliveryReadyPeopleCount,
    },
    DELIVERY: {
      count: data.deliveryCompletedCount,
      peopleCount: data.deliveryCompletedPeopleCount,
    },
    CANCEL: {
      count: data.orderCancelledCount,
      peopleCount: data.orderCancelledPeopleCount,
    },
    REFUND_HOLD_CANCEL: {
      count: data.refundHoldCancelCount,
      peopleCount: data.refundHoldCancelPeopleCount,
    },
    REFUND_HOLD_RETURN: {
      count: data.refundHoldReturnCount,
      peopleCount: data.refundHoldReturnPeopleCount,
    },
    REFUND: {
      count: data.orderRefundedCount,
      peopleCount: data.orderRefundedPeopleCount,
    },
    TOTAL: {
      count: data.totalOrderCount,
      peopleCount: data.totalPeopleCount,
    },
  };

  return result;
};

export const orderTableHeaders: Record<OrderStatusWithTotal, any> = {
  REQUEST: {
    label: "신규주문",
    order: 1,
    keys: ["orderCode", "productName", "memo"],
  },
  DEPOSIT_WAIT: {
    label: "입금대기",
    order: 2,
    keys: ["orderCode", "productName", "memo"],
  },
  COMPLETE: {
    label: "결제완료",
    order: 3,
    keys: ["orderCode", "productName", "memo"],
  },
  DELIVERY_READY: {
    label: "배송준비",
    order: 4,
    keys: ["orderCode", "productName", "memo"],
  },
  DELIVERY: {
    label: "배송완료",
    order: 5,
    keys: ["orderCode", "productName", "memo"],
  },
  CANCEL: {
    label: "주문취소",
    order: 6,
    keys: ["orderCode", "productName", "memo"],
  },
  REFUND_HOLD_CANCEL: {
    label: "환불보류-취소",
    order: 7,
    keys: ["orderCode", "productName", "memo"],
  },
  REFUND_HOLD_RETURN: {
    label: "환불보류-반품",
    order: 8,
    keys: ["orderCode", "productName", "memo"],
  },
  REFUND: {
    label: "환불완료",
    order: 9,
    keys: ["orderCode", "productName", "memo"],
  },
  TOTAL: {
    label: "전체",
    order: 10,
    keys: ["orderCode", "productName", "memo"],
  },
};

export function mapTodosToRows(
  status: OrderStatusWithTotal,
  todos: IDetailTodo[]
): TableRow[] {
  const keys = statusColumnKeys[status];
  let rows: TableRow[] = [];
  const noMergeCols = new Set([
    "productName",
    "productId",
    "option",
    "quantity",
    "price",
    "totalPrice",
    "refundAmount",
  ]);

  const shouldMergeCols = new Set([
    "deliveryReadyAt",
    "refundHoldAt",
    "deliveredAt",
    "statusUpdatedAt",
    "cancelledAt",
    "orderedAt",
    "refundedAt",
    "status",
    "cancelReason",
    "cancelDetailReason",
    "additionalFee",
    "returnFee",
    "refundType",
    "refundMethod",
    "refundProcess",
    "cancelFailureReason",
  ]);
  // 배송비, 제주도서산간 배송비는
  // 신규주문, 결제완료, 배송준비, 배송완료 탭에서는 게스트 단위로 그룹화 되어야 함
  if (
    status !== "REQUEST" &&
    status !== "COMPLETE" &&
    status !== "DELIVERY_READY" &&
    status !== "DELIVERY"
  ) {
    shouldMergeCols.add("shippingFee");
    shouldMergeCols.add("remoteFee");
  }
  if (
    status !== "REQUEST" &&
    status !== "COMPLETE" &&
    status !== "DELIVERY_READY" &&
    status !== "DELIVERY" &&
    status !== "REFUND_HOLD_CANCEL" &&
    status !== "REFUND_HOLD_RETURN"
  ) {
    shouldMergeCols.add("paymentMethod");
  }

  todos.forEach((todo) => {
    const originOrders = todo.orders;
    const fullMergeSize = originOrders.length;

    const ordersGroupsByChangedUUid = originOrders.reduce((acc, order) => {
      let key: string;
      if (status === "REQUEST") {
        // REQUEST 상태일 경우 guestId로 그룹화
        key = todo.guestId?.toString() ?? order.status;
      } else {
        // 기존 로직: changedUuid가 없으면 status로 그룹화
        key = order.changedUuid ?? order.status;
      }
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(order);
      return acc;
    }, {} as Record<string, any[]>);

    // changedUuid로 그룹화된 주문들을 순회
    Object.values(ordersGroupsByChangedUUid).forEach((orders, i) => {
      orders.forEach((order, j) => {
        const values: Record<string, any> = {};
        const rowSpan: Record<string, number> = {};
        const GroupMergeSize = orders.length;

        keys.forEach((key) => {
          // 값 매핑 (기존 switch 로직 축약)
          let val: any;
          switch (key) {
            case "checkbox":
              val = false;
              break;
            case "orderId":
              val = order.orderCode;
              break;
            case "nickname":
              val = todo.nickname;
              break;
            // 결제일
            case "paidAt":
              val =
                todo.payment?.approvedAt ??
                order.statusUpdatedAt ??
                order.updatedAt ??
                "";
              break;
            case "deliveryReadyAt": // 배송 준비일
            case "deliveredAt": // 배송 완료 일
            case "statusUpdatedAt": // 상태 변경일
            case "refundHoldAt": // 환불 보류일
            case "refundedAt": // 환불 일
            case "cancelledAt": // 취소일
              val = order.statusUpdatedAt ?? order.updatedAt ?? "";
              break;
            // 주문일
            case "orderedAt":
              val = order.createdAt;
              break;

            case "status":
              val = order.status;
              break;
            case "productName":
              val = order.productName;
              break;
            case "productId":
              val = order.number;
              break;
            case "option":
              val = order.productOption;
              break;
            case "quantity":
              val = order.count;
              break;
            case "price":
              val = order.price;
              break;
            case "totalPrice":
              val = order.totalPrice;
              break;
            case "refundAmount":
              break;
            case "actualPaidAmount":
              break;
            case "expectedAmount":
            case "finalPaidAmount":
              val = todo.totalPayment;
              break;
            case "receiverName":
              val = todo.name;
              break;
            case "receiverPhone":
              val = todo.shippingPhone;
              break;
            case "address1":
              val = todo.newAddress ?? todo.address ?? "";
              break;
            case "address2":
              val = todo.newAddressDetail ?? "";
              break;
            case "zipcode":
              val = todo.zonecode ?? "";
              break;
            case "ordererPhone":
              val = todo.phone;
              break;
            case "memo":
              val = todo.memo;
              break;
            case "trackingNumber":
              val = `${order.deliveryCompany ?? ""} ${
                order.deliveryInvoice ?? ""
              }`;
              break;
            case "sellerMemo":
              val = todo.memo2;
              break;
            case "remoteFee":
              break;
            case "cancelDetailReason":
              val = order.cancelReason;
              break;
            case "paymentDueDate":
              val = order.paymentDeadline;
              break;
            case "paymentMethod":
              val = todo.payment?.method;
              break;
            case "refundType":
              val = order.refundType;
              break;
            case "refundMethod":
              val = order.refundMethod;
              break;
            case "cancelFailureReason":
              val = order.cancelFailureReason;
              break;
            default:
              val = (todo as any)[key] ?? (order as any)[key] ?? "";
          }
          values[key] = val;
          values["uuid"] = order.changedUuid ?? order.orderCode;

          if (noMergeCols.has(key)) {
            rowSpan[key] = 1;
          } else if (shouldMergeCols.has(key)) {
            // 해시별 병합이 필요한 경우
            rowSpan[key] = j === 0 ? GroupMergeSize : 0;
          } else {
            // 전체 병합이 필요한 경우
            rowSpan[key] = i === 0 && j === 0 ? fullMergeSize : 0;
          }

          if (status === "TOTAL") {
            if (key === "orderedAt") rowSpan[key] = 1;
            if (key === "status") rowSpan[key] = 1;
            if (key === "statusUpdatedAt") rowSpan[key] = 1;

            if (key === "shippingFee")
              rowSpan[key] = i === 0 && j === 0 ? fullMergeSize : 0;
            if (key === "remoteFee")
              rowSpan[key] = i === 0 && j === 0 ? fullMergeSize : 0;
            if (key === "paymentMethod")
              rowSpan[key] = i === 0 && j === 0 ? fullMergeSize : 0;
          }
        });
        rows.push({
          values,
          rowSpan,
          original: {
            todo,
            order,
          },
        });
      });
    });
  });
  // console.log('rows', rows)
  return rows;
}
